const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initDarkMile() {
  const hero = document.querySelector('[data-dark-mile-hero]');
  if (hero) {
    if (reducedMotion.matches) {
      hero.classList.add('is-ready');
    } else {
      requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add('is-ready')));
    }
  }

  document.addEventListener('pointerdown', (event) => {
    const control = event.target.closest('button, .button, .dark-mile-link-button, [role="radio"]');
    if (control) control.classList.add('arcane-is-pressed');
  });

  document.addEventListener('pointerup', () => {
    document.querySelectorAll('.arcane-is-pressed').forEach((control) => control.classList.remove('arcane-is-pressed'));
  });

  document.addEventListener('pointercancel', () => {
    document.querySelectorAll('.arcane-is-pressed').forEach((control) => control.classList.remove('arcane-is-pressed'));
  });

  document.addEventListener('click', (event) => {
    const choice = event.target.closest('variant-picker button, .variant-option button, [role="radio"], quantity-selector button');
    if (!choice || reducedMotion.matches) return;
    choice.classList.remove('arcane-choice-pulse');
    requestAnimationFrame(() => choice.classList.add('arcane-choice-pulse'));
  });

  const stateObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type !== 'attributes') return;
      const target = mutation.target;
      if (target.matches?.('[data-added="true"]')) target.classList.add('arcane-added');
      if (target.matches?.('dialog[open], details[open]')) target.classList.add('arcane-is-open');
    });
  });
  stateObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'data-added'] });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDarkMile, { once: true });
} else {
  initDarkMile();
}

function setupDarkMileRoute(scope = document) {
  scope.querySelectorAll('[data-dark-mile-route]').forEach((route) => {
    if (route.dataset.routeReady === 'true') return;
    const section = route.closest('.dark-mile-product-edit');
    const rail = section?.querySelector('[data-dark-mile-rail]');
    const stops = [...route.querySelectorAll('[data-dark-mile-stop]')];
    const products = [...section?.querySelectorAll('[data-dark-mile-product]') || []];
    if (!rail || stops.length === 0 || products.length === 0) return;

    route.dataset.routeReady = 'true';
    let frame = 0;

    const activate = (index) => {
      const safeIndex = Math.max(0, Math.min(index, products.length - 1));
      stops.forEach((stop, stopIndex) => stop.setAttribute('aria-pressed', String(stopIndex === safeIndex)));
      products.forEach((product, productIndex) => product.classList.toggle('is-active', productIndex === safeIndex));
      const progress = products.length > 1 ? (safeIndex / (products.length - 1)) * 100 : 0;
      route.style.setProperty('--dark-mile-route-progress', `${progress}%`);
    };

    stops.forEach((stop) => {
      const index = Number(stop.dataset.index);
      stop.addEventListener('pointerenter', () => activate(index));
      stop.addEventListener('focus', () => activate(index));
      stop.addEventListener('click', () => {
        activate(index);
        if (window.matchMedia('(max-width: 990px)').matches) {
          products[index]?.scrollIntoView({
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      });
    });

    products.forEach((product) => {
      const index = Number(product.dataset.index);
      product.addEventListener('pointerenter', () => activate(index));
      product.addEventListener('focusin', () => activate(index));
    });

    rail.addEventListener('scroll', () => {
      if (!window.matchMedia('(max-width: 990px)').matches || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const railCenter = rail.getBoundingClientRect().left + rail.clientWidth / 2;
        let nearestIndex = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;
        products.forEach((product, index) => {
          const rect = product.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - railCenter);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });
        activate(nearestIndex);
      });
    }, { passive: true });

    activate(0);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setupDarkMileRoute(), { once: true });
} else {
  setupDarkMileRoute();
}

document.addEventListener('shopify:section:load', (event) => setupDarkMileRoute(event.target));

function setupDarkMileScrollMotion(scope = document) {
  const items = [...scope.querySelectorAll('[data-dark-mile-reveal]')];
  if (!items.length || reducedMotion.matches) {
    items.forEach((item) => item.classList.add('is-visible'));
    return;
  }

  document.documentElement.classList.add('dark-mile-motion-ready');
  const revealObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  items.forEach((item) => revealObserver.observe(item));

  const hero = scope.querySelector('[data-dark-mile-hero]');
  if (hero && !window.matchMedia('(max-width: 720px)').matches) {
    let scheduled = false;
    const updateHero = () => {
      scheduled = false;
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const travel = Math.max(-28, Math.min(28, rect.top * -0.035));
      hero.style.setProperty('--dark-mile-parallax', `${travel}px`);
    };
    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(updateHero);
    };
    updateHero();
    window.addEventListener('scroll', onScroll, { passive: true });
  }
}

function setupDarkMileCatalogue(scope = document) {
  scope.querySelectorAll('[data-dark-mile-sort]').forEach((select) => {
    if (select.dataset.sortReady === 'true') return;
    select.dataset.sortReady = 'true';
    select.addEventListener('change', () => select.form?.submit());
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setupDarkMileScrollMotion();
    setupDarkMileCatalogue();
  }, { once: true });
} else {
  setupDarkMileScrollMotion();
  setupDarkMileCatalogue();
}

document.addEventListener('shopify:section:load', (event) => {
  setupDarkMileScrollMotion(event.target);
  setupDarkMileCatalogue(event.target);
});

function setupDarkMileProductControls(scope = document) {
  scope.querySelectorAll('quantity-selector-component').forEach((selector) => {
    if (selector.dataset.darkMileMotion === 'true') return;
    selector.dataset.darkMileMotion = 'true';
    const quantityInput = selector.querySelector('input[name="quantity"]');
    selector.querySelectorAll('button[name="minus"], button[name="plus"]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!quantityInput || reducedMotion.matches) return;
        selector.style.setProperty('--dark-mile-quantity-shift', button.name === 'plus' ? '8px' : '-8px');
        selector.classList.remove('dark-mile-quantity-changed');
        requestAnimationFrame(() => selector.classList.add('dark-mile-quantity-changed'));
      });
    });
    selector.addEventListener('animationend', () => selector.classList.remove('dark-mile-quantity-changed'));
  });

  scope.querySelectorAll('.template-product .add-to-cart-button, .product-details .add-to-cart-button').forEach((button) => {
    if (button.dataset.darkMileMotion === 'true') return;
    button.dataset.darkMileMotion = 'true';
    button.addEventListener('click', () => {
      if (button.disabled || reducedMotion.matches) return;
      button.classList.remove('dark-mile-add-sweep');
      requestAnimationFrame(() => button.classList.add('dark-mile-add-sweep'));
    });
    button.addEventListener('animationend', () => button.classList.remove('dark-mile-add-sweep'));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setupDarkMileProductControls(), { once: true });
} else {
  setupDarkMileProductControls();
}

document.addEventListener('shopify:section:load', (event) => setupDarkMileProductControls(event.target));

